import { BaseElement, Editor, Element, Range, Transforms } from 'slate'
import { AtOptions } from './helpers'
import { PluginFn } from './withPlugins'

export type BlockAlignment = 'left' | 'center' | 'right'

export interface BlockElement extends BaseElement {
    type: 'paragraph' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5'
    alignment?: BlockAlignment
}

interface WithBlocksOptions {
    defaultType: BlockElement['type']
}

/**
 * - Handles return-to-default-block-type-on-enter
 * - Handles alignment for all block types
 */
export function withBlocks({ defaultType }: WithBlocksOptions): PluginFn {
    return (editor) => {
        const { normalizeNode, insertBreak } = editor

        editor.normalizeNode = ([node, path]) => {
            if (Block.isBlock(node) && !node.alignment) {
                Transforms.setNodes(editor, { alignment: 'left' }, { at: path })
                return
            }

            if (Editor.isEditor(node) && !node.children.length) {
                Transforms.insertNodes(editor, { type: defaultType, children: [{ text: '' }] })
                return
            }

            normalizeNode([node, path])
        }

        editor.insertBreak = () => {
            if (!editor.selection) {
                return
            }

            const selection = editor.selection // preserve selection
            insertBreak()

            let at = editor.selection.anchor.path
            // if the cursor was at the beginning of a block, set the current block to default rather than the new one
            if (Range.isCollapsed(selection) && selection.anchor.offset === 0) {
                at = selection.anchor.path
            }

            // remove the last item from the path because its the text node
            Transforms.setNodes(editor, { type: defaultType }, { at: at.slice(0, at.length - 1) })
        }

        return editor
    }
}

export const Block = {
    isBlock(elem: any): elem is BlockElement {
        return (
            Element.isElement(elem) &&
            (elem.type === 'paragraph' ||
                elem.type === 'h1' ||
                elem.type === 'h2' ||
                elem.type === 'h3' ||
                elem.type === 'h4' ||
                elem.type === 'h5')
        )
    },

    getAlignment(editor: Editor, options: AtOptions = {}) {
        const { at = editor.selection } = options

        if (!at) {
            return
        }

        const [match] = Editor.nodes(editor, { at, match: Block.isBlock })
        return match?.[0].alignment ?? null
    },

    setAlignment(editor: Editor, alignment: BlockAlignment, options: AtOptions = {}) {
        const { at = editor.selection } = options

        if (!at) {
            return
        }

        Transforms.setNodes(editor, { alignment }, { at })
    },
}
